import { apiClient, WHOLE_USE_MOCK } from './api';
import { mockDataUtil } from './mockDataUtil';
import {roles} from "../mock/mockRoleData";
import {API_ENDPOINTS} from "../utils/apiEndpoints";
import { menuService } from './menuService';
import { current } from '@reduxjs/toolkit';

class RoleService {
  /**
   * 获取角色列表
   */
  async getRoles(params) {
    if (await apiClient.getMock() && WHOLE_USE_MOCK) {
      // 如果是查询特定账号的角色
      if (params.authQueryType === "1" && params.authedAccount) {
        // 导入账号数据来获取账号的角色信息
        const { accounts } = await import('../mock/mockAccountData.js');
        const account = accounts.find(a => a.loginId === params.authedAccount);
        
        if (account && account.roleList) {
          // 根据账号的角色代码，从所有角色中筛选出对应的角色详细信息
          const accountRoles = account.roleList.map(accountRole => {
            const fullRole = roles.find(r => r.code === accountRole.code);
            return fullRole || {
              code: accountRole.code,
              name: accountRole.name,
              id: Date.now() + Math.random()
            };
          });
          
          return mockDataUtil.mockPageRes(accountRoles, {
            currentPage: 1,
            totalCount: accountRoles.length,
            pageSize: accountRoles.length
          });
        } else {
          return mockDataUtil.mockPageRes([], {
            currentPage: 1,
            totalCount: 0,
            pageSize: 10
          });
        }
      }
      
      // 正常的角色列表查询
      let filteredRoles = [...roles];

      // 根据查询条件过滤
      if (params.name) {
        filteredRoles = filteredRoles.filter(role =>
            role.name.toLowerCase().includes(params.name.toLowerCase())
        );
      }

      if (params.code) {
        filteredRoles = filteredRoles.filter(role =>
            role.code.toLowerCase().includes(params.code.toLowerCase())
        );
      }

      const pageSize = params.pageSize || 10;
      const currentPage = params.currentPage || 1;
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedRoles = filteredRoles.slice(startIndex, endIndex);

      return mockDataUtil.mockPageRes(paginatedRoles, {
        currentPage: currentPage,
        totalCount: filteredRoles.length,
        pageSize: pageSize
      });
    } else {
      let req = {...params};
      if (req.name) {
        req.nameLike = req.name;
      }
      return apiClient.post(API_ENDPOINTS.ROLE.PAGE, req);
    }
  }

  /**
   * 创建角色
   */
  async createRole(role) {
    if (await apiClient.getMock() && WHOLE_USE_MOCK) {
      const newRole = {
        ...role,
        id: roles.length + 1,
        creatorName: "当前用户",
        creatorAccount: "admin",
        gmtCreate: new Date().getTime(),
        gmtModified: new Date().getTime(),
        deleted: false,
        menuCodes: role.menuCodes || []
      };

      roles.push(newRole);

      return {
        traceId: "65df5c9972284947a150aa90ed9a41b3",
        success: true,
        msg: null,
        errorCode: null,
        errorMessage: null,
        flag: "success",
        code: null,
        message: null,
        properties: {},
        data: newRole
      };
    } else {
      return apiClient.post(API_ENDPOINTS.ROLE.CREATE, role);
    }
  }

  /**
   * 更新角色
   */
  async updateRole(role) {
    if (await apiClient.getMock() && WHOLE_USE_MOCK) {

      const index = roles.findIndex(r => r.id === role.id);
      if (index === -1) {
        return {
          traceId: "65df5c9972284947a150aa90ed9a41b3",
          success: false,
          errorMessage: "角色不存在",
          flag: "fail",
          data: null
        };
      }

      const updatedRole = {
        ...roles[index],
        ...role,
        modifierName: "当前用户",
        modifierAccount: "admin",
        gmtModified: new Date().getTime(),
        menuCodes: role.menuCodes !== undefined ? role.menuCodes : roles[index].menuCodes
      };

      roles[index] = updatedRole;

      return {
        traceId: "65df5c9972284947a150aa90ed9a41b3",
        success: true,
        msg: null,
        errorCode: null,
        errorMessage: null,
        flag: "success",
        code: null,
        message: null,
        properties: {},
        data: updatedRole
      };
    } else {
      return apiClient.post(API_ENDPOINTS.ROLE.UPDATE, role);
    }
  }

  /**
   * 删除角色
   */
  async deleteRole(code) {
    if (await apiClient.getMock() && WHOLE_USE_MOCK) {
      const index = roles.findIndex(r => r.code === code);
      if (index === -1) {
        return {
          traceId: "65df5c9972284947a150aa90ed9a41b3",
          success: false,
          errorMessage: "角色不存在",
          flag: "fail",
          data: null
        };
      }

      roles.splice(index, 1);

      return mockDataUtil.mockRes()
    } else {
      return apiClient.post(API_ENDPOINTS.ROLE.DELETE, { code });
    }
  }

  /**
   * 获取角色的菜单权限
   */
  async getRoleMenuPermissions(roleCode) {
    if (await apiClient.getMock() && WHOLE_USE_MOCK) {
      const role = roles.find(r => r.code === roleCode);
      if (!role) {
        return {
          traceId: "65df5c9972284947a150aa90ed9a41b3",
          success: false,
          errorMessage: "角色不存在",
          flag: "fail",
          data: null
        };
      }

      return {
        traceId: "65df5c9972284947a150aa90ed9a41b3",
        success: true,
        msg: null,
        errorCode: null,
        errorMessage: null,
        flag: "success",
        code: null,
        message: null,
        properties: {},
        data: {
          roleCode: role.code,
          menuCodes: role.menuCodes || []
        }
      };
    } else {
      return apiClient.post(API_ENDPOINTS.ROLE.GET_MENU_PERMISSIONS, { roleCode });
    }
  }

  /**
   * 更新角色的菜单权限
   */
  async updateRoleMenuPermissions(roleCode, menuCodes) {
    if (await apiClient.getMock() && WHOLE_USE_MOCK) {
      const index = roles.findIndex(r => r.code === roleCode);
      if (index === -1) {
        return {
          traceId: "65df5c9972284947a150aa90ed9a41b3",
          success: false,
          errorMessage: "角色不存在",
          flag: "fail",
          data: null
        };
      }

      roles[index] = {
        ...roles[index],
        menuCodes: menuCodes || [],
        modifierName: "当前用户",
        modifierAccount: "admin",
        gmtModified: new Date().getTime()
      };

      return {
        traceId: "65df5c9972284947a150aa90ed9a41b3",
        success: true,
        msg: null,
        errorCode: null,
        errorMessage: null,
        flag: "success",
        code: null,
        message: null,
        properties: {},
        data: {
          roleCode: roles[index].code,
          menuCodes: roles[index].menuCodes
        }
      };
    } else {
      return apiClient.post(API_ENDPOINTS.ROLE.UPDATE_MENU_PERMISSIONS, { code: roleCode, authedMenuCodes: menuCodes });
    }
  }

  /**
   * 获取所有可用菜单列表
   */
  async getAvailableMenus(appDomain) {
    return await menuService.getMenuTree({ appDomain: appDomain });
  }

   async getAuthedMenu(roleCode, appDomain) {
    return apiClient.post(API_ENDPOINTS.ROLE.QUERY_AUTHED_PERMISSIONS, {appDomain: appDomain, authedRole: roleCode, currentPage:1 , pageSize:200});
  }
}

export const roleService = new RoleService(); 